import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import GlobalStyles from './styles/GlobalStyles';
import MainLayout from './components/Layout/MainLayout';
import AlbumGrid from './components/AlbumGrid/AlbumGrid';
import AlbumDetail from './components/AlbumDetail/AlbumDetail';
import ShelfSettings from './components/Settings/ShelfSettings';
import SyncStatus from './components/Settings/SyncStatus';
import CollectionSyncModal from './components/Sync/CollectionSyncModal';
import ArtistEnhancementModal from './components/Enhancement/ArtistEnhancementModal';
import ITunesCollectionModal from './components/Enhancement/ITunesCollectionModal';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginScreen from './components/Auth/LoginScreen';
import AdminToggle from './components/Auth/AdminToggle';
import { Album, FilterState } from './types/album';
import { ShelfConfig, DEFAULT_SHELF_CONFIG } from './utils/shelfCalculation';
import { secureSupabaseService } from './services/secureSupabaseService';
import { sortAlbums } from './utils/sorting';
import { calculateShelfPositions } from './utils/shelfCalculation';
import { getUniqueGenres, getUniqueArtists, getUniqueDecades, getUniqueCountries, getUniqueLanguages, getYearRange } from './utils/filterUtils';
import { useWindowSize } from './hooks/useWindowSize';

// Using Supabase for shared database persistence

function AppContent() {
  const { isAuthenticated, login, isAdmin } = useAuth();
  const { isMobile } = useWindowSize();
  const [albums, setAlbums] = React.useState<Album[]>([]);
  const [filters, setFilters] = React.useState<FilterState>({
    searchText: '',
    genres: [],
    yearRange: [1950, new Date().getFullYear()],
    artists: [],
    filterMode: 'any',
    decades: [],
    countries: [],
    languages: [],
  });
  // Start with filters closed on mobile, open on desktop
  const [showFilters, setShowFilters] = React.useState(!isMobile);
  const [selectedAlbum, setSelectedAlbum] = React.useState<Album | null>(null);
  const [showShelfSettings, setShowShelfSettings] = React.useState(false);
  const [showSyncStatus, setShowSyncStatus] = React.useState(false);
  const [showAlbumSync, setShowAlbumSync] = React.useState(false);
  const [showArtistEnhancement, setShowArtistEnhancement] = React.useState(false);
  const [showITunesEnhancement, setShowITunesEnhancement] = React.useState(false);
  const [shelfConfig, setShelfConfig] = React.useState<ShelfConfig>(DEFAULT_SHELF_CONFIG);

  // Load data on startup
  React.useEffect(() => {
    const loadAppData = async () => {
      try {
        // Initialize Supabase storage
        await secureSupabaseService.initialize();
        
        // Run data migration for multiple artists (no-op for Supabase)
        await secureSupabaseService.migrateToMultipleArtists();
        
        // Load existing collection data from Supabase
        const savedAlbums = await secureSupabaseService.getAllAlbums();
        if (savedAlbums.length > 0) {
          console.log(`Loaded ${savedAlbums.length} albums from Supabase`);
          // Images are already loaded by the Supabase service
          // Sort albums (keep existing shelf positions if they exist)
          const sortedAlbums = sortAlbums(savedAlbums);
          setAlbums(sortedAlbums);
        } else {
          console.log('No saved albums found, starting with empty collection');
        }

        // Load shelf configuration and Discogs config
        const settings = await secureSupabaseService.getSettings();
        // Handle both camelCase and snake_case field names
        const shelfConfigData = settings.shelfConfig || settings.shelf_config;
        if (shelfConfigData) {
          setShelfConfig(shelfConfigData);
          console.log('Loaded shelf configuration from Supabase');
        }
        
        // Load and set Discogs configuration if available
        if (settings.discogs_username && settings.discogs_token) {
          const discogsConfig = {
            username: settings.discogs_username,
            token: settings.discogs_token,
            baseUrl: 'https://api.discogs.com'
          };
          // Set the config in the service so it's ready to use
          const { discogsService } = await import('./services/discogsService');
          discogsService.setConfig(discogsConfig);
          console.log('Loaded Discogs configuration from Supabase');
        }
      } catch (error) {
        console.error('Could not load data on startup:', error);
        // If loading fails, start with empty collection
        setAlbums([]);
      } finally {
        // Initialization complete
      }
    };
    
    loadAppData();
  }, []);

  // Extract unique values for filter options
  const availableGenres = React.useMemo(() => getUniqueGenres(albums), [albums]);
  const availableArtists = React.useMemo(() => getUniqueArtists(albums), [albums]);
  const availableDecades = React.useMemo(() => getUniqueDecades(albums), [albums]);
  const availableCountries = React.useMemo(() => getUniqueCountries(albums), [albums]);
  const availableLanguages = React.useMemo(() => getUniqueLanguages(albums), [albums]);
  const albumYearRange = React.useMemo(() => getYearRange(albums), [albums]);

  // Helper function to find matching tracks for search
  const getMatchingTracks = React.useCallback((album: Album, searchText: string): string[] => {
    if (!searchText || !album.tracklist) return [];
    
    const searchLower = searchText.toLowerCase();
    return album.tracklist
      .filter(track => track.title.toLowerCase().includes(searchLower))
      .map(track => track.title);
  }, []);

  // Filter albums based on current filters
  const filteredAlbums = React.useMemo(() => {
    const filtered = albums.filter(album => {
      // Search text filter
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase();
        let matchesSearch = 
          album.artist.toLowerCase().includes(searchLower) ||
          album.title.toLowerCase().includes(searchLower) ||
          (album.artists && album.artists.some(artist => artist.toLowerCase().includes(searchLower)));
        
        // Also search track titles
        if (!matchesSearch && album.tracklist) {
          matchesSearch = album.tracklist.some(track => 
            track.title.toLowerCase().includes(searchLower)
          );
        }
        
        if (!matchesSearch) return false;
      }

      // Genre filter
      if (filters.genres.length > 0) {
        if (filters.filterMode === 'any') {
          const hasGenre = album.genres.some(genre => filters.genres.includes(genre));
          if (!hasGenre) return false;
        } else {
          // 'all' mode: album must have ALL selected genres
          const hasAllGenres = filters.genres.every(selectedGenre => 
            album.genres.includes(selectedGenre)
          );
          if (!hasAllGenres) return false;
        }
      }

      // Year range filter
      if (album.year < filters.yearRange[0] || album.year > filters.yearRange[1]) {
        return false;
      }

      // Artist filter
      if (filters.artists.length > 0) {
        const albumArtists = album.artists || [album.artist];
        const hasArtist = albumArtists.some(artist => 
          filters.artists.some(filterArtist => 
            artist.toLowerCase().includes(filterArtist.toLowerCase())
          )
        );
        if (!hasArtist) return false;
      }

      // Decade filter
      if (filters.decades.length > 0) {
        const albumDecade = Math.floor(album.year / 10) * 10 + 's'; // e.g., "1970s"
        if (!filters.decades.includes(albumDecade)) return false;
      }

      // Country filter (artist origin)
      if (filters.countries.length > 0) {
        if (!album.artistCountry || !filters.countries.includes(album.artistCountry)) {
          return false;
        }
      }

      // Language filter
      if (filters.languages.length > 0) {
        if (!album.language || !filters.languages.includes(album.language)) {
          return false;
        }
      }

      return true;
    });
    
    // Sort the filtered albums
    return sortAlbums(filtered);
  }, [albums, filters]);

  const handleAlbumClick = (album: Album) => {
    setSelectedAlbum(album);
  };

  const handleAlbumUpdate = async (updatedAlbum: Album) => {
    // Save to Supabase first
    try {
      await secureSupabaseService.saveAlbum(updatedAlbum);
      console.log(`Saved album update to Supabase: ${updatedAlbum.artist} - ${updatedAlbum.title}`);
    } catch (error) {
      console.error('Failed to save album update:', error);
    }
    
    // Then update state
    setAlbums((prev: Album[]) => {
      const updated = prev.map((album: Album) => 
        album.id === updatedAlbum.id ? updatedAlbum : album
      );
      return sortAlbums(updated);
    });
    setSelectedAlbum(updatedAlbum);
  };

  const handleShelfConfigUpdate = async (newConfig: ShelfConfig) => {
    setShelfConfig(newConfig);
    try {
      // Save shelf config to Supabase settings
      await secureSupabaseService.updateSettings({
        shelf_config: newConfig  // Use snake_case to match database field
      });
      console.log('Shelf configuration saved to Supabase');
    } catch (error) {
      console.error('Failed to save shelf configuration:', error);
    }
  };

  const handleAlbumsAdded = (newAlbums: Album[]) => {
    // Replace entire collection with new albums from sync
    console.log(`Updating app state with ${newAlbums.length} albums from sync`);
    const sortedAlbums = sortAlbums(newAlbums);
    const albumsWithPositions = calculateShelfPositions(sortedAlbums, shelfConfig);
    setAlbums(albumsWithPositions);
    setShowAlbumSync(false);
    setShowSyncStatus(false);
  };


  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen onLogin={login} />;
  }

  return (
    <Router>
      <div className="App">
        <GlobalStyles />
        <AdminToggle />
        <Routes>
          <Route path="/" element={
            <MainLayout
              filters={filters}
              availableGenres={availableGenres}
              availableArtists={availableArtists}
              availableDecades={availableDecades}
              availableCountries={availableCountries}
              availableLanguages={availableLanguages}
              yearRange={albumYearRange}
              onFiltersChange={setFilters}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              albumCount={albums.length}
              onShowSyncStatus={() => isAdmin && setShowSyncStatus(true)}
              showSyncButton={isAdmin}
            >
              <AlbumGrid 
                albums={filteredAlbums} 
                onAlbumClick={handleAlbumClick}
                searchText={filters.searchText}
                getMatchingTracks={(album) => getMatchingTracks(album, filters.searchText || '')}
              />
            </MainLayout>
          } />
        </Routes>
        
        {selectedAlbum && (
          <AlbumDetail
            album={selectedAlbum}
            albums={albums}
            filteredAlbums={filteredAlbums}
            shelfConfig={shelfConfig}
            onAlbumChange={setSelectedAlbum}
            onAlbumUpdate={handleAlbumUpdate}
            onClose={() => setSelectedAlbum(null)}
            onShowShelfSettings={() => isAdmin && setShowShelfSettings(true)}
            showShelfSettings={isAdmin}
          />
        )}

        {showShelfSettings && (
          <ShelfSettings
            albums={albums}
            config={shelfConfig}
            onConfigChange={handleShelfConfigUpdate}
            onClose={() => setShowShelfSettings(false)}
          />
        )}

        {showSyncStatus && (
          <SyncStatus
            albumCount={albums.length}
            onClose={() => setShowSyncStatus(false)}
            onStartSync={() => setShowAlbumSync(true)}
            onStartArtistEnhancement={() => setShowArtistEnhancement(true)}
            onStartITunesEnhancement={() => setShowITunesEnhancement(true)}
          />
        )}

        {showAlbumSync && (
          <CollectionSyncModal
            onClose={() => setShowAlbumSync(false)}
            onAlbumsAdded={handleAlbumsAdded}
            existingAlbums={albums}
          />
        )}

        {showArtistEnhancement && (
          <ArtistEnhancementModal
            albums={albums}
            onClose={() => setShowArtistEnhancement(false)}
            onAlbumsUpdated={(updatedAlbums) => {
              const sortedAlbums = sortAlbums(updatedAlbums);
              const albumsWithPositions = calculateShelfPositions(sortedAlbums, shelfConfig);
              setAlbums(albumsWithPositions);
            }}
          />
        )}

        {showITunesEnhancement && (
          <ITunesCollectionModal
            albums={albums}
            onClose={() => setShowITunesEnhancement(false)}
            onAlbumsUpdated={(updatedAlbums) => {
              const sortedAlbums = sortAlbums(updatedAlbums);
              const albumsWithPositions = calculateShelfPositions(sortedAlbums, shelfConfig);
              setAlbums(albumsWithPositions);
              setShowITunesEnhancement(false);
            }}
          />
        )}


      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
